import { Link } from "@inertiajs/react";
import type { ReactNode } from "react";

export type Column<T> = {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
};

export type RowAction<T> = {
  label: string;
  href?: (row: T) => string;
  onClick?: (row: T) => void;
  variant?: "primary" | "danger";
  // File downloads (exports, etc.) need a real browser navigation — an Inertia
  // Link would intercept it as an SPA visit and render the binary response as
  // page content instead of downloading it.
  external?: boolean;
  // Reserves the action's space without rendering it as clickable — use this
  // (instead of leaving the action out) for a condition that varies per row
  // (e.g. "Confirmar" only for pending rows), so every row's action group is
  // the same width and they line up with each other instead of each row's
  // narrower group re-centering.
  hidden?: boolean;
};

export type PagyMeta = {
  page: number;
  page_size: number;
  count: number;
  pages: number;
  next: number | null;
  prev: number | null;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  keyExtractor: (row: T) => string | number;
  actions?: RowAction<T>[] | ((row: T) => RowAction<T>[]);
  utilities?: ReactNode;
  emptyMessage?: string;
  pagy?: PagyMeta;
  onPageChange?: (page: number) => void;
};

const ACTION_VARIANTS = {
  primary: "bg-white border-ink text-ink hover:bg-ink/5",
  danger: "bg-deep-red border-deep-red text-white hover:bg-deep-red/90",
};

const ELLIPSIS = "…";

// Builds a compact page list like [1, 2, 3, "…", 8, 9, 10, "…", 18, 19, 20],
// always keeping a window of boundary pages at each edge and a window
// around the current page visible.
function pageItems(page: number, pages: number): (number | typeof ELLIPSIS)[] {
  const siblings = 1;
  const boundary = 3;
  const items: (number | typeof ELLIPSIS)[] = [];

  const startPages = Array.from(
    { length: Math.min(boundary, pages) },
    (_, i) => i + 1,
  );
  const endPages = Array.from(
    { length: Math.min(boundary, pages) },
    (_, i) => pages - i,
  ).reverse();

  const start = Math.max(boundary + 1, page - siblings);
  const end = Math.min(pages - boundary, page + siblings);

  items.push(...startPages);
  if (start > boundary + 1) items.push(ELLIPSIS);
  for (let p = start; p <= end; p++) {
    if (p > boundary && p < pages - boundary + 1) items.push(p);
  }
  if (end < pages - boundary) items.push(ELLIPSIS);
  items.push(...endPages.filter((p) => p > boundary));

  return items;
}

export default function DataTable<T>({
  columns,
  rows,
  keyExtractor,
  actions,
  utilities,
  emptyMessage = "No hay registros.",
  pagy,
  onPageChange,
}: DataTableProps<T>) {
  const hasActions = Array.isArray(actions)
    ? actions.length > 0
    : Boolean(actions);
  const colCount = columns.length + (hasActions ? 1 : 0);
  // Match the height driver of real rows: action buttons are h-6, plain text is h-4 (text-xs line-height)
  const fillerInnerHeight = hasActions ? "h-6" : "h-4";

  return (
    <div className="w-full">
      {utilities && (
        <div className="mb-4 flex flex-wrap justify-end gap-6 px-6 sm:px-8 lg:px-12">
          {utilities}
        </div>
      )}

      <div className="w-full overflow-x-auto px-1">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {columns.map((col, i) => {
                const isLastCol = i === columns.length - 1 && !hasActions;
                return (
                  <th
                    key={i}
                    className={[
                      "bg-steel px-3 py-2 text-left font-sans text-xs font-semibold text-white",
                      i === 0 ? "rounded-l-full pl-5" : "",
                      isLastCol
                        ? "rounded-r-full pr-5"
                        : "border-r border-white/30",
                      col.className ?? "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {col.header}
                  </th>
                );
              })}
              {hasActions && (
                <th className="rounded-r-full bg-steel px-3 py-2 pr-5 text-center font-sans text-xs font-semibold text-white">
                  Acciones
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  className="py-8 text-center font-sans text-xs font-light text-ink/60"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {rows.map((row, rowIdx) => (
              <tr
                key={keyExtractor(row)}
                className={rowIdx % 2 === 1 ? "bg-[#C0CE2E]/16" : ""}
              >
                {columns.map((col, ci) => {
                  const isLastCol = ci === columns.length - 1 && !hasActions;
                  return (
                    <td
                      key={ci}
                      className={[
                        "px-3 py-2 font-sans text-xs font-light text-ink",
                        ci === 0 ? "pl-5" : "",
                        isLastCol ? "pr-5" : "border-r border-ink/10",
                        col.className ?? "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {col.cell(row)}
                    </td>
                  );
                })}
                {hasActions && (
                  <td className="px-3 py-2 pr-5 last:pr-5">
                    <div className="flex flex-wrap justify-center gap-2">
                      {(typeof actions === "function"
                        ? actions(row)
                        : actions
                      )?.map((action) => {
                        const variant = action.variant ?? "danger";
                        const cls = `inline-flex h-6 items-center whitespace-nowrap rounded-full border-2 px-2.5 font-sans text-[11px] font-semibold uppercase tracking-wide transition-colors ${ACTION_VARIANTS[variant]}`;

                        if (action.hidden) {
                          return (
                            <span
                              key={action.label}
                              aria-hidden="true"
                              className={`${cls} invisible`}
                            >
                              {action.label}
                            </span>
                          );
                        }

                        if (action.href && action.external) {
                          return (
                            <a
                              key={action.label}
                              href={action.href(row)}
                              className={cls}
                            >
                              {action.label}
                            </a>
                          );
                        }

                        if (action.href) {
                          return (
                            <Link
                              key={action.label}
                              href={action.href(row)}
                              className={cls}
                            >
                              {action.label}
                            </Link>
                          );
                        }

                        return (
                          <button
                            key={action.label}
                            type="button"
                            onClick={() => action.onClick?.(row)}
                            className={cls}
                          >
                            {action.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {pagy &&
              // The empty-message row above occupies one visual slot when there
              // are no rows, so the filler only needs to pad the rest of the page.
              Array.from(
                {
                  length: Math.max(
                    pagy.page_size - rows.length - (rows.length === 0 ? 1 : 0),
                    0,
                  ),
                },
                (_, i) => (
                  <tr
                    // biome-ignore lint/suspicious/noArrayIndexKey: filler rows are stateless placeholders identified only by position
                    key={`filler-${i}`}
                    className={
                      (rows.length + (rows.length === 0 ? 1 : 0) + i) % 2 === 1
                        ? "bg-[#C0CE2E]/16"
                        : ""
                    }
                  >
                    {Array.from({ length: colCount }, (_, ci) => (
                      <td
                        // biome-ignore lint/suspicious/noArrayIndexKey: empty filler cells have no identity beyond their column position
                        key={ci}
                        className={[
                          "px-3 py-2",
                          ci === 0 ? "pl-5" : "",
                          ci === colCount - 1
                            ? "pr-5"
                            : "border-r border-ink/10",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {ci === 0 && (
                          <div
                            aria-hidden="true"
                            className={fillerInnerHeight}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ),
              )}
          </tbody>
        </table>
      </div>

      {pagy && onPageChange && (
        <div className="mt-4 flex items-center justify-between px-6 sm:px-8 lg:px-12">
          <span className="font-sans text-xs font-light text-ink/60">
            {pagy.count} registros · página {pagy.page} de {pagy.pages}
          </span>
          {pagy.pages > 1 && (
            <div className="flex flex-wrap items-center justify-end gap-1">
              <button
                type="button"
                disabled={pagy.page === 1}
                onClick={() => onPageChange(1)}
                title="Primera página"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-steel font-sans text-xs text-steel transition-colors hover:bg-steel hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                «
              </button>
              <button
                type="button"
                disabled={pagy.prev === null}
                onClick={() => pagy.prev !== null && onPageChange(pagy.prev)}
                title="Página anterior"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-steel font-sans text-xs text-steel transition-colors hover:bg-steel hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                ‹
              </button>

              {pageItems(pagy.page, pagy.pages).map((item, i) =>
                item === ELLIPSIS ? (
                  <span
                    // biome-ignore lint/suspicious/noArrayIndexKey: ellipsis markers are stateless placeholders identified only by position
                    key={`ellipsis-${i}`}
                    className="inline-flex h-7 w-7 items-center justify-center font-sans text-xs text-ink/40"
                  >
                    {ELLIPSIS}
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    disabled={item === pagy.page}
                    onClick={() => onPageChange(item)}
                    className={[
                      "inline-flex h-7 w-7 items-center justify-center rounded-full border-2 font-sans text-xs transition-colors disabled:cursor-default",
                      item === pagy.page
                        ? "border-steel bg-steel text-white"
                        : "border-steel text-steel hover:bg-steel hover:text-white",
                    ].join(" ")}
                  >
                    {item}
                  </button>
                ),
              )}

              <button
                type="button"
                disabled={pagy.next === null}
                onClick={() => pagy.next !== null && onPageChange(pagy.next)}
                title="Página siguiente"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-steel font-sans text-xs text-steel transition-colors hover:bg-steel hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                ›
              </button>
              <button
                type="button"
                disabled={pagy.page === pagy.pages}
                onClick={() => onPageChange(pagy.pages)}
                title="Última página"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-steel font-sans text-xs text-steel transition-colors hover:bg-steel hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
